import { MenuItem, TextField } from "@mui/material";
import { controlBandFieldSx, compactMenuProps } from "../../theme/compact.js";
import type { ReactElement, ReactNode } from "react";

/**
 * `<SingleSelectFilter>` — canonical single-select pill filter.
 *
 * Spec: `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 5 /
 * SingleSelectFilter".
 *
 * - `<TextField select>` with `displayEmpty` + `renderValue`.
 * - 130 px wide.
 * - "All" / "none" option renders an empty display string so the field
 *   is blank with just the floating label when no narrowing filter is
 *   applied. Pass `{ value: "all", label: "All", display: "" }` as the
 *   first option (or set `display: ""` on whichever option represents
 *   "no filter").
 * - Optional `endAdornment` slot for in-field icons (e.g. info-icon
 *   popover triggers, mirroring the Data Acquisitions Status filter
 *   pattern). Renders before MUI's Select chevron; bump `width` ~40 px
 *   when supplied so the chevron + icon both have breathing room.
 */
export interface SingleSelectFilterOption {
  value: string;
  label: string;
  /** Visible value text in the closed state. Defaults to `label`. Set
   *  to `""` for the "All" / "no filter" option so the field stays
   *  blank when that option is selected. */
  display?: string;
}

export interface SingleSelectFilterProps {
  label: string;
  value: string;
  options: readonly SingleSelectFilterOption[];
  onChange: (next: string) => void;
  /** Per-page width override. Defaults to 130 px (spec). */
  width?: number;
  /** Optional in-field icon slot. Rendered before MUI's Select
   *  chevron via `slotProps.select.endAdornment`. Use for info-icon
   *  popover triggers or similar inline affordances. The caller is
   *  responsible for the icon's click handler + any out-of-field
   *  popover sibling. */
  endAdornment?: ReactNode;
}

export function SingleSelectFilter({
  label,
  value,
  options,
  onChange,
  width = 130,
  endAdornment,
}: SingleSelectFilterProps): ReactElement {
  const findDisplay = (v: unknown): string => {
    const opt = options.find((o) => o.value === v);
    if (!opt) return "";
    return opt.display ?? opt.label;
  };
  return (
    <TextField
      select
      label={label}
      size="small"
      value={value}
      sx={{
        width,
        flexShrink: 0,
        // The dense band (D77 compact, 32px). Without this the control
        // takes MUI's `size="small"` default, which is 40px for a field —
        // not one of the three sanctioned sizes, simply whatever the
        // library does. Audit, 2026-09-23.
        ...controlBandFieldSx,
      }}
      slotProps={{
        select: {
          displayEmpty: true,
          // The menu renders through a portal, so it cannot inherit the
          // field's density and has to be told separately.
          MenuProps: compactMenuProps,
          renderValue: findDisplay,
          onChange: (e) => {
            onChange(String(e.target.value));
          },
          // `endAdornment` on the Select itself (not on the outer
          // Input) so the icon sits between the rendered value and
          // the chevron — same visual slot the Data Acquisitions
          // Status filter uses, just via the Select API instead of
          // Autocomplete's. The chevron stays at the absolute right
          // edge unchanged.
          ...(endAdornment !== undefined ? { endAdornment } : {}),
        },
        inputLabel: { shrink: true },
      }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
